/**
 * "Manual Agreement" badge for a collab-held schedule line.
 *
 * A schedule is held once an accepted date/qty proposal moved it off the
 * value the ERP still sends. ERP imports leave it alone and the auto-close
 * reconcile skips it, so the badge tells the reader two things at once: the
 * line carries a Helix-only agreement, and it will not close itself. Shared
 * by the Scheduled Lines row, the GRN Repair card and the dashboard
 * drilldown tables so every surface names the state the same way.
 *
 * Deliberately NOT "On Hold" — that is a different fact (a customer/supplier
 * stop) with its own badge and its own release rule.
 */
import { RefreshCw } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';

/** The slice of POLineSchedule the badge reads — lets drilldown rows reuse it. */
export interface ManualAgreementTarget {
  collab_hold?: boolean;
  collab_hold_since?: string | null;
  collab_erp_date?: string | null;
  collab_agreed_date?: string | null;
  collab_agreed_qty?: string | null;
}

interface ManualAgreementBadgeProps {
  schedule: ManualAgreementTarget;
  /** Compact variant for dense tables (no icon). */
  compact?: boolean;
  className?: string;
}

/**
 * Tooltip text for a held line — the agreement, what the ERP still sends,
 * and why the line will not auto-close. Exported so the close dialog can
 * show the same facts.
 */
export function describeManualAgreement(
  s: ManualAgreementTarget,
  formatDate: (d: string | Date) => string,
): string {
  const agreed = [
    s.collab_agreed_date ? formatDate(s.collab_agreed_date) : null,
    s.collab_agreed_qty != null ? `qty ${parseFloat(s.collab_agreed_qty)}` : null,
  ].filter(Boolean).join(' × ');
  const parts = [
    'Manual agreement: a date/qty change agreed in Helix that the ERP does not carry.',
    agreed ? `Agreed ${agreed}.` : null,
    // A qty-only agreement keeps the ERP's date; naming it again says nothing.
    s.collab_erp_date && s.collab_erp_date !== s.collab_agreed_date
      ? `ERP still sends ${formatDate(s.collab_erp_date)}.` : null,
    s.collab_hold_since ? `Held since ${formatDate(s.collab_hold_since)}.` : null,
    'Auto-close skips this line.',
  ];
  return parts.filter(Boolean).join(' ');
}

/**
 * Renders nothing unless the line is collab-held.
 */
export default function ManualAgreementBadge({ schedule, compact = false, className = '' }: ManualAgreementBadgeProps) {
  const { formatDate } = useFormatters();
  if (!schedule.collab_hold) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 px-2 py-0.5 text-xs font-medium whitespace-nowrap ${className}`}
      title={describeManualAgreement(schedule, formatDate)}
      data-testid="manual-agreement-badge"
    >
      {!compact && <RefreshCw className="h-3 w-3" />}
      Manual Agreement
    </span>
  );
}
